import type { Principal } from '@cerbos/core';

import { canonicalJson } from './canonical-json';

/**
 * Principal attributes any policy in `Skynet-Trading/Cerbos.Config` branches
 * on, as read off the policy sources: `P.attr.tenant` and `P.attr.permissions`
 * (the `Auth/*` policies), `P.attr.userId` and `P.attr.crc` (`Auth/User`).
 * `status` is keyed too — the `User` policy reads `R.attr.status`, and a
 * principal-side counterpart is cheap to cover and easy to miss.
 *
 * This is an allowlist rather than everything-but-the-volatile-claims because
 * `getPrincipal` spreads the whole JWT payload into `attr`: keying on `attr`
 * wholesale would tie every cached decision to `exp`/`iat`, which change on
 * every silent token refresh, and drop the cache several times an hour for a
 * principal whose authorization inputs never moved.
 *
 * Adding a `P.attr.<name>` to a policy therefore means adding `<name>` here —
 * a decision cached against a key that omits it is not invalidated when it
 * changes.
 */
const KEYED_ATTRIBUTES = ['tenant', 'permissions', 'userId', 'crc', 'status'] as const;

/**
 * Sorts an attribute that is a list, and leaves anything else alone.
 *
 * Every keyed attribute that is a list is a *set* to the policies that read it
 * — `permissions` is tested with `in`, never by position — but `canonicalJson`
 * preserves array order, because order carries meaning in general. Two tokens
 * granting the same permissions in a different order would otherwise key the
 * principal differently and miss the cache: same answer, extra PDP request,
 * and the blink back on screen while it is in flight.
 */
const asSet = (value: unknown): unknown =>
  Array.isArray(value) ? Array.from<unknown>(value).sort(compareCanonically) : value;

/** Orders two attribute entries by their canonical form, whatever they are. */
const compareCanonically = (left: unknown, right: unknown): number => {
  const leftKey = canonicalJson(left);
  const rightKey = canonicalJson(right);

  if (leftKey === rightKey) return 0;
  return leftKey < rightKey ? -1 : 1;
};

/**
 * Builds a stable cache key for a principal: its id, roles, and the attributes
 * listed in {@link KEYED_ATTRIBUTES}. Decisions cached against this key are
 * invalidated whenever any of them change — not only when the user id does, so
 * switching tenant or gaining/losing a role re-asks the PDP. Roles and any
 * list-valued attribute are keyed as sets (see {@link asSet}), so a token that
 * lists the same grants in a different order keys the same principal.
 *
 * The policies behind the checks opted into the cache today (`Accounts`,
 * `UIOverviewTabs`, `RetainerOverview`, `LoanExchanges`, `LoanKPI`, `Chart`,
 * `GetReport`) branch on roles alone and read no principal attributes at all;
 * the attributes above are keyed so that opting another check in later does not
 * silently start serving stale answers.
 *
 * Two caches key on this: the decision cache in `check-cache.ts`, and the
 * navigation permissions held in Redux (`root-middleware`). Both live in
 * memory only, so widening what the key covers costs at most one extra fetch
 * on the first load after a deploy — it never leaves a stored key behind that
 * something later mistakes for a match.
 */
export const getPrincipalKey = (principal: Principal): string => {
  const attr = principal.attr ?? {};

  return canonicalJson({
    id: principal.id ?? null,
    roles: [...(principal.roles ?? [])].sort(),
    attr: Object.fromEntries(
      KEYED_ATTRIBUTES.filter((name) => attr[name] !== undefined).map((name) => [
        name,
        asSet(attr[name]),
      ]),
    ),
  });
};
